import L from 'leaflet';

const removeLegacyReadinessActions = () => {
    const readinessPanel = document.querySelector('[data-venue-map-readiness-panel]');
    const communityMapLink = readinessPanel?.querySelector(
        'a[href*="/venue-map/community-suggestions"]'
    );
    const legacyActionRow = communityMapLink?.closest(
        'div.mt-4.flex.flex-wrap.gap-2'
    );

    legacyActionRow?.remove();
};

const initializeUnifiedPrivatePinComposer = () => {
    const panel = document.querySelector('[data-live-map-add-to-map]');

    if (! panel) {
        return;
    }

    removeLegacyReadinessActions();

    if (panel.dataset.unifiedPrivatePinInitialized === 'true') {
        return;
    }

    const toggle = panel.querySelector('[data-unified-private-pin-toggle]');
    const composer = panel.querySelector('[data-unified-private-pin-composer]');
    const close = composer?.querySelector('[data-unified-private-pin-close]');
    const canvas = composer?.querySelector('[data-unified-private-pin-map]');
    const currentLocation = composer?.querySelector('[data-unified-private-pin-current-location]');
    const form = composer?.querySelector('[data-unified-private-pin-form]');
    const typeInput = composer?.querySelector('[data-unified-private-pin-type]');
    const labelInput = composer?.querySelector('[data-unified-private-pin-label]');
    const noteInput = composer?.querySelector('[data-unified-private-pin-note]');
    const status = composer?.querySelector('[data-unified-private-pin-status]');
    const submit = composer?.querySelector('[data-unified-private-pin-submit]');

    if (! toggle || ! composer || ! canvas || ! form) {
        return;
    }

    const latitude = Number.parseFloat(panel.dataset.festivalLatitude || '');
    const longitude = Number.parseFloat(panel.dataset.festivalLongitude || '');
    const storeUrl = panel.dataset.personalPinStoreUrl || '';
    const duplicateCheckUrl = panel.dataset.personalPinDuplicateCheckUrl || '';

    if (! Number.isFinite(latitude) || ! Number.isFinite(longitude) || ! storeUrl) {
        return;
    }

    panel.dataset.unifiedPrivatePinInitialized = 'true';

    let map = null;
    let marker = null;
    let selectedLatitude = latitude;
    let selectedLongitude = longitude;

    const setStatus = (message, state = 'ready') => {
        if (! status) {
            return;
        }

        status.textContent = message;
        status.dataset.state = state;
    };

    const setPoint = (lat, lng) => {
        selectedLatitude = lat;
        selectedLongitude = lng;

        if (! marker && map) {
            marker = L.marker([lat, lng]).addTo(map);
        } else {
            marker?.setLatLng([lat, lng]);
        }

        setStatus('Private spot selected. Add a label, then save it.');
    };

    const openComposer = () => {
        composer.classList.remove('hidden');
        toggle.setAttribute('aria-expanded', 'true');

        if (! map) {
            map = L.map(canvas).setView([latitude, longitude], 16);

            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; OpenStreetMap contributors',
                maxZoom: 19,
            }).addTo(map);

            setPoint(latitude, longitude);
            map.on('click', (event) => setPoint(event.latlng.lat, event.latlng.lng));
        }

        window.setTimeout(() => {
            map?.invalidateSize();
            labelInput?.focus();
        }, 0);
    };

    const closeComposer = () => {
        composer.classList.add('hidden');
        toggle.setAttribute('aria-expanded', 'false');
        toggle.focus();
    };

    const checkForDuplicate = async (pinType, label) => {
        if (! duplicateCheckUrl) {
            return false;
        }

        const url = new URL(duplicateCheckUrl, window.location.origin);
        url.searchParams.set('pin_type', pinType);
        url.searchParams.set('label', label);
        url.searchParams.set('latitude', String(selectedLatitude));
        url.searchParams.set('longitude', String(selectedLongitude));

        const response = await fetch(url.toString(), {
            headers: {
                Accept: 'application/json',
            },
            credentials: 'same-origin',
        });

        const payload = await response.json().catch(() => ({}));

        if (! response.ok) {
            throw new Error(payload?.message || 'FestFriends could not check nearby private spots.');
        }

        return payload?.data?.strong_duplicate === true;
    };

    toggle.addEventListener('click', () => {
        if (composer.classList.contains('hidden')) {
            openComposer();
        } else {
            closeComposer();
        }
    });

    close?.addEventListener('click', closeComposer);

    composer.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            closeComposer();
        }
    });

    currentLocation?.addEventListener('click', () => {
        if (! navigator.geolocation) {
            setStatus('Current location is not available in this browser. Tap the map instead.', 'error');
            return;
        }

        currentLocation.setAttribute('disabled', 'disabled');
        setStatus('Finding your current location for this private pin…', 'locating');

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const lat = position.coords.latitude;
                const lng = position.coords.longitude;

                setPoint(lat, lng);
                map?.setView([lat, lng], Math.max(map.getZoom(), 17));
                setStatus('Current location selected for this private pin. Nothing is being shared live.', 'located');
                currentLocation.removeAttribute('disabled');
            },
            () => {
                setStatus('FestFriends could not access your current location. You can still tap the map.', 'error');
                currentLocation.removeAttribute('disabled');
            },
            {
                enableHighAccuracy: true,
                maximumAge: 15000,
                timeout: 10000,
            }
        );
    });

    form.addEventListener('submit', async (event) => {
        event.preventDefault();

        const pinType = typeInput?.value || 'custom';
        const label = labelInput?.value.trim() || '';
        const note = noteInput?.value.trim() || '';

        if (! label) {
            setStatus('Add a label before saving this private spot.', 'error');
            labelInput?.focus();
            return;
        }

        submit?.setAttribute('disabled', 'disabled');
        setStatus('Checking nearby private spots…', 'checking');

        try {
            const strongDuplicate = await checkForDuplicate(pinType, label);
            let acknowledgeNearbyPrivatePin = false;

            if (strongDuplicate) {
                acknowledgeNearbyPrivatePin = window.confirm(
                    'You already have a very similar private pin at this spot. Save another one anyway?'
                );

                if (! acknowledgeNearbyPrivatePin) {
                    setStatus('Existing private spot kept. Nothing new was saved.', 'duplicate');
                    return;
                }
            }

            setStatus('Saving your private spot…', 'saving');

            const response = await fetch(storeUrl, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || '',
                },
                credentials: 'same-origin',
                body: JSON.stringify({
                    pin_type: pinType,
                    label,
                    note: note || null,
                    latitude: selectedLatitude,
                    longitude: selectedLongitude,
                    acknowledge_nearby_private_pin: acknowledgeNearbyPrivatePin,
                }),
            });

            const payload = await response.json().catch(() => ({}));

            if (! response.ok) {
                throw new Error(payload?.message || 'This private spot could not be saved.');
            }

            setStatus('Private spot saved. Only you can see it.', 'saved');
            form.reset();

            window.dispatchEvent(new CustomEvent('festfriends:private-pin-saved', {
                detail: payload?.data || null,
            }));
        } catch (error) {
            setStatus(error instanceof Error ? error.message : 'This private spot could not be saved.', 'error');
        } finally {
            submit?.removeAttribute('disabled');
        }
    });
};

document.addEventListener('DOMContentLoaded', initializeUnifiedPrivatePinComposer);
document.addEventListener('livewire:navigated', initializeUnifiedPrivatePinComposer);
